import React, { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { Button } from '@/components/residents/marketplace/shared/Button';
import {
  useGetSurveyResultsQuery,
  useSubmitSurveyResponseMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { PollQuestionView } from '@/components/residents/corkBoard/survey/PollQuestionView';
import { RatingQuestionView } from '@/components/residents/corkBoard/survey/RatingQuestionView';
import { LongTextQuestionView } from '@/components/residents/corkBoard/survey/LongTextQuestionView';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useToast } from '@/contexts/admin/ToastContext';
import type { QuestionResult, SubmitSurveyResponseRequest } from '@/types/residents/corkBoard';

interface Props {
  threadId: string;
  onSubmitted?: () => void;
}

interface DraftAnswer {
  questionId: number;
  optionId?: number;
  ratingValue?: number;
  textValue?: string;
}

export const SurveyResponseScreen: React.FC<Props> = ({ threadId, onSubmitted }) => {
  const { colors } = useThemeColors();
  const { showError } = useToast();
  const { data, isLoading, error } = useGetSurveyResultsQuery(threadId);
  const [submit, submitState] = useSubmitSurveyResponseMutation();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<number, DraftAnswer>>({});

  const questions = data?.questions ?? [];
  const current = questions[currentIdx];

  const isAnswered = useMemo(() => {
    if (!current) return false;
    const a = answers[current.questionId];
    if (!a) return false;
    if (current.type === 'poll') return a.optionId !== undefined;
    if (current.type === 'rating') return a.ratingValue !== undefined;
    if (current.type === 'long_text') return (a.textValue ?? '').trim().length > 0;
    return false;
  }, [current, answers]);

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.newsfeed.tagActiveText} />
      </View>
    );
  }
  if (error || !data) {
    return (
      <View style={styles.centered}>
        <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.secondary }]}>
          Unable to load survey.
        </Text>
      </View>
    );
  }
  if (questions.length === 0) {
    return (
      <View style={styles.centered}>
        <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.secondary }]}>
          This survey has no questions.
        </Text>
      </View>
    );
  }

  const canAdvance = !current || isAnswered || !current.isRequired;
  const isLast = currentIdx === questions.length - 1;

  const setAnswer = (patch: Partial<DraftAnswer>) => {
    setAnswers((prev) => ({
      ...prev,
      [current.questionId]: {
        ...prev[current.questionId],
        questionId: current.questionId,
        ...patch,
      },
    }));
  };

  const handleSubmit = async () => {
    const payload: SubmitSurveyResponseRequest = {
      threadId,
      answers: Object.values(answers).map(({ questionId, optionId, ratingValue, textValue }) => ({
        questionId,
        ...(optionId !== undefined ? { optionId } : {}),
        ...(ratingValue !== undefined ? { ratingValue } : {}),
        ...(textValue !== undefined ? { textValue } : {}),
      })),
    };
    try {
      await submit(payload).unwrap();
    } catch (e) {
      showError('Could not submit your response. Please try again.');
      return;
    }
    onSubmitted?.();
  };

  const onNext = () => (isLast ? handleSubmit() : setCurrentIdx((i) => i + 1));
  const onBack = () => setCurrentIdx((i) => Math.max(0, i - 1));
  const onSkip = onNext;

  return (
    <View style={styles.container}>
      <View style={[styles.progressBar, { backgroundColor: colors.card.border }]}>
        <View
          style={[
            styles.progressFill,
            {
              width: `${((currentIdx + 1) / questions.length) * 100}%`,
              backgroundColor: colors.newsfeed.tagActiveText,
            },
          ]}
        />
      </View>
      <Text
        style={[
          REDESIGN_TYPOGRAPHY.small,
          { color: colors.text.secondary, marginBottom: REDESIGN_SPACING.contentGap },
        ]}
      >
        Question {currentIdx + 1} of {questions.length}
      </Text>

      <GlassCard
        variant="soft"
        borderRadius={REDESIGN_SPACING.cardBorderRadius}
        padding={REDESIGN_SPACING.cardPadding}
      >
        <QuestionRenderer
          question={current}
          answer={answers[current.questionId]}
          onChange={setAnswer}
        />
      </GlassCard>

      <View style={styles.nav}>
        <Button
          title="Back"
          variant="outline"
          size="medium"
          disabled={currentIdx === 0}
          onPress={onBack}
          style={styles.navBtn}
        />
        {!current.isRequired && !isAnswered ? (
          <Button
            title="Skip"
            variant="secondary"
            size="medium"
            onPress={onSkip}
            style={styles.navBtn}
          />
        ) : (
          <View style={styles.navBtn} />
        )}
        <Button
          title={isLast ? 'Submit' : 'Next'}
          variant="primary"
          size="medium"
          isLoading={isLast && submitState.isLoading}
          disabled={!canAdvance || submitState.isLoading}
          onPress={onNext}
          style={styles.navBtn}
        />
      </View>
    </View>
  );
};

const QuestionRenderer: React.FC<{
  question: QuestionResult;
  answer: DraftAnswer | undefined;
  onChange: (patch: Partial<DraftAnswer>) => void;
}> = ({ question, answer, onChange }) => {
  if (question.type === 'poll') {
    const opts = question.options ?? [];
    return (
      <PollQuestionView
        prompt={question.prompt}
        options={opts.map((o) => ({ id: o.optionId, label: o.label }))}
        selectedOptionId={answer?.optionId}
        onChange={(optionId) => onChange({ optionId })}
      />
    );
  }
  if (question.type === 'rating') {
    return (
      <RatingQuestionView
        prompt={question.prompt}
        value={answer?.ratingValue}
        onChange={(ratingValue) => onChange({ ratingValue })}
      />
    );
  }
  return (
    <LongTextQuestionView
      prompt={question.prompt}
      value={answer?.textValue ?? ''}
      onChange={(textValue) => onChange({ textValue })}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: REDESIGN_SPACING.horizontalPadding,
    gap: REDESIGN_SPACING.contentGap,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: REDESIGN_SPACING.horizontalPadding,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  nav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: REDESIGN_SPACING.sectionGap,
    marginTop: REDESIGN_SPACING.contentGap,
  },
  navBtn: { flex: 1 },
});
